import type { Coordinate } from '../types';

export const NORMAL_WEATHER_REFRESH_MS = 60 * 60 * 1000;
export const ECLIPSE_DAY_WEATHER_REFRESH_MS = 15 * 60 * 1000;
export const FORECAST_FRAME_TOLERANCE_MS = 90 * 60 * 1000;

export interface WeatherBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface WeatherFrame {
  validAt: string;
  forecastHour: number;
  url: string;
  width: number;
  height: number;
}

export interface WeatherManifest {
  version: 2;
  model: string;
  modelRun: string;
  generatedAt: string;
  bounds: WeatherBounds;
  resolutionKm: number;
  cloudEncoding: {
    channel: 'alpha';
    opacityExponent: number;
  };
  frames: WeatherFrame[];
  attribution: {
    name: string;
    license: string;
    url: string;
  };
}

export interface WeatherLayer {
  frame: WeatherFrame;
  bounds: WeatherBounds;
  opacityExponent: number;
}

export interface CloudReading extends Coordinate {
  cloudCover: number;
  validAt: string;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function isWeatherFrame(value: unknown): value is WeatherFrame {
  if (!value || typeof value !== 'object') return false;
  const frame = value as Partial<WeatherFrame>;
  return (
    isIsoDate(frame.validAt) &&
    Number.isInteger(frame.forecastHour) &&
    typeof frame.url === 'string' &&
    /^\/weather\/cloud-[a-zA-Z0-9-]+\.png$/.test(frame.url) &&
    Number.isInteger(frame.width) &&
    Number(frame.width) > 1 &&
    Number.isInteger(frame.height) &&
    Number(frame.height) > 1
  );
}

export function isWeatherManifest(value: unknown): value is WeatherManifest {
  if (!value || typeof value !== 'object') return false;
  const manifest = value as Partial<WeatherManifest>;
  const bounds = manifest.bounds as Partial<WeatherBounds> | undefined;
  const attribution = manifest.attribution as Partial<WeatherManifest['attribution']> | undefined;
  return (
    manifest.version === 2 &&
    typeof manifest.model === 'string' &&
    isIsoDate(manifest.modelRun) &&
    isIsoDate(manifest.generatedAt) &&
    !!bounds &&
    isFiniteNumber(bounds.west) &&
    isFiniteNumber(bounds.south) &&
    isFiniteNumber(bounds.east) &&
    isFiniteNumber(bounds.north) &&
    bounds.west < bounds.east &&
    bounds.south < bounds.north &&
    isFiniteNumber(manifest.resolutionKm) &&
    manifest.resolutionKm > 0 &&
    manifest.cloudEncoding?.channel === 'alpha' &&
    isFiniteNumber(manifest.cloudEncoding.opacityExponent) &&
    manifest.cloudEncoding.opacityExponent >= 0.2 &&
    manifest.cloudEncoding.opacityExponent <= 1 &&
    Array.isArray(manifest.frames) &&
    manifest.frames.every(isWeatherFrame) &&
    !!attribution &&
    typeof attribution.name === 'string' &&
    typeof attribution.license === 'string' &&
    typeof attribution.url === 'string'
  );
}

export async function fetchWeatherManifest(): Promise<WeatherManifest> {
  const refreshBucket = Math.floor(Date.now() / (5 * 60 * 1000));
  const response = await fetch(`/weather/manifest.json?v=${refreshBucket}`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Weather manifest returned ${response.status}`);
  const data: unknown = await response.json();
  if (!isWeatherManifest(data)) throw new Error('Weather manifest is invalid');
  return data;
}

export function selectWeatherFrame(
  manifest: WeatherManifest,
  target: Date,
  toleranceMs: number,
): WeatherFrame | null {
  const targetTime = target.getTime();
  let closest: WeatherFrame | null = null;
  let closestDifference = Number.POSITIVE_INFINITY;
  for (const frame of manifest.frames) {
    const difference = Math.abs(Date.parse(frame.validAt) - targetTime);
    if (difference < closestDifference) {
      closest = frame;
      closestDifference = difference;
    }
  }
  return closestDifference <= toleranceMs ? closest : null;
}

export function weatherRefreshInterval(now: Date): number {
  const isEclipseDay =
    now.getUTCFullYear() === 2026 && now.getUTCMonth() === 7 && now.getUTCDate() === 12;
  return isEclipseDay ? ECLIPSE_DAY_WEATHER_REFRESH_MS : NORMAL_WEATHER_REFRESH_MS;
}

export function coordinateToPixel(
  coordinate: Coordinate,
  bounds: WeatherBounds,
  width: number,
  height: number,
): { x: number; y: number } | null {
  if (
    coordinate.lon < bounds.west ||
    coordinate.lon > bounds.east ||
    coordinate.lat < bounds.south ||
    coordinate.lat > bounds.north
  ) {
    return null;
  }
  return {
    x: ((coordinate.lon - bounds.west) / (bounds.east - bounds.west)) * (width - 1),
    y: ((bounds.north - coordinate.lat) / (bounds.north - bounds.south)) * (height - 1),
  };
}

export function decodeCloudAlpha(alpha: number, opacityExponent: number): number {
  if (alpha <= 0) return 0;
  return 100 * (Math.min(255, alpha) / 255) ** (1 / opacityExponent);
}

export function bilinearInterpolate(
  topLeft: number,
  topRight: number,
  bottomLeft: number,
  bottomRight: number,
  horizontal: number,
  vertical: number,
): number {
  const top = topLeft + (topRight - topLeft) * horizontal;
  const bottom = bottomLeft + (bottomRight - bottomLeft) * horizontal;
  return top + (bottom - top) * vertical;
}

const pixelCache = new Map<string, Promise<ImageData>>();

function loadFramePixels(frame: WeatherFrame): Promise<ImageData> {
  const cached = pixelCache.get(frame.url);
  if (cached) return cached;
  const request = new Promise<ImageData>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = frame.width;
      canvas.height = frame.height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        reject(new Error('Canvas is unavailable'));
        return;
      }
      context.drawImage(image, 0, 0, frame.width, frame.height);
      resolve(context.getImageData(0, 0, frame.width, frame.height));
    };
    image.onerror = () => reject(new Error('Cloud raster could not be loaded'));
    image.src = frame.url;
  }).catch((error: unknown) => {
    pixelCache.delete(frame.url);
    throw error;
  });
  pixelCache.set(frame.url, request);
  return request;
}

export async function cloudAtLocation(
  layer: WeatherLayer,
  location: Coordinate,
): Promise<CloudReading | null> {
  const pixel = coordinateToPixel(location, layer.bounds, layer.frame.width, layer.frame.height);
  if (!pixel) return null;
  const image = await loadFramePixels(layer.frame);
  const left = Math.floor(pixel.x);
  const right = Math.min(layer.frame.width - 1, Math.ceil(pixel.x));
  const top = Math.floor(pixel.y);
  const bottom = Math.min(layer.frame.height - 1, Math.ceil(pixel.y));
  const readCover = (x: number, y: number) => {
    const alpha = image.data[(y * layer.frame.width + x) * 4 + 3];
    return decodeCloudAlpha(alpha ?? 0, layer.opacityExponent);
  };
  const cloudCover = bilinearInterpolate(
    readCover(left, top),
    readCover(right, top),
    readCover(left, bottom),
    readCover(right, bottom),
    pixel.x - left,
    pixel.y - top,
  );
  return {
    ...location,
    cloudCover: Math.round(cloudCover),
    validAt: layer.frame.validAt,
  };
}
